import type { SupabaseClient } from "@supabase/supabase-js";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { WORKER_URL } from "../prospects/enrichFromSource";
import { enrichLeadFields, fieldsToProspectRow } from "./leadEnrich";
import type { DealLead } from "./types";

export interface CrmContext {
  sb: SupabaseClient;
  salesId: number;
}

/** Resolve the signed-in user's `sales` row (null when signed out). */
export const getSessionAndSale = async (): Promise<CrmContext | null> => {
  const sb = getSupabaseClient();
  const {
    data: { session },
  } = await sb.auth.getSession();
  if (!session) return null;
  const { data: sale } = await sb
    .from("sales")
    .select("id")
    .eq("user_id", session.user.id)
    .maybeSingle();
  return sale ? { sb, salesId: sale.id } : null;
};

/** Run `fn` over items with bounded concurrency. */
export const runPool = async <T>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<void>,
): Promise<void> => {
  let i = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (i < items.length) {
        const idx = i++;
        await fn(items[idx], idx);
      }
    },
  );
  await Promise.all(workers);
};

export const findProspect = async (
  ctx: CrmContext,
  item: Pick<DealLead, "id">,
): Promise<Record<string, any> | null> => {
  const { data } = await ctx.sb
    .from("prospects")
    .select("*")
    .eq("sales_id", ctx.salesId)
    .eq("source_record_id", String(item.id))
    .limit(1);
  return data?.[0] ?? null;
};

export const findProspectByApn = async (
  ctx: CrmContext,
  apn: string,
): Promise<Record<string, any> | null> => {
  const { data } = await ctx.sb
    .from("prospects")
    .select("*")
    .eq("sales_id", ctx.salesId)
    .eq("parcel_apn", apn)
    .limit(1);
  return data?.[0] ?? null;
};

/** Ensure the lead exists as a prospect; returns its id (creating if missing). */
export const ensureProspect = async (
  ctx: CrmContext,
  item: DealLead,
): Promise<number | null> => {
  const existing = await findProspect(ctx, item);
  if (existing) return existing.id;
  const row = fieldsToProspectRow(await enrichLeadFields(item), ctx.salesId);
  const { data, error } = await ctx.sb
    .from("prospects")
    .insert(row)
    .select("id")
    .single();
  if (error) {
    if (error.code === "23505") {
      const p = await findProspect(ctx, item); // raced with another insert
      return p ? p.id : null;
    }
    console.error("ensureProspect insert", error);
    return null;
  }
  return data.id;
};

// ===== Lists (the `groups` table, surfaced as "Lists") =====

export interface DealList {
  id: number;
  name: string;
  color: string | null;
}

/** Quick lists that always appear; created on demand when first chosen. */
export const DEFAULT_LISTS = ["Auction", "Probate", "ALL"];

export const fetchLists = async (): Promise<DealList[]> => {
  const ctx = await getSessionAndSale();
  if (!ctx) return [];
  const { data } = await ctx.sb
    .from("groups")
    .select("id,name,color")
    .eq("sales_id", ctx.salesId)
    .order("name");
  return data ?? [];
};

/** Find-or-create a list by name (unique on sales_id,name). */
export const createList = async (name: string): Promise<DealList | null> => {
  const ctx = await getSessionAndSale();
  if (!ctx) return null;
  const trimmed = name.trim();
  const { data, error } = await ctx.sb
    .from("groups")
    .insert({ sales_id: ctx.salesId, name: trimmed })
    .select("id,name,color")
    .single();
  if (!error) return data;
  if (error.code === "23505") {
    const { data: existing } = await ctx.sb
      .from("groups")
      .select("id,name,color")
      .eq("sales_id", ctx.salesId)
      .eq("name", trimmed)
      .limit(1);
    return existing?.[0] ?? null;
  }
  console.error("createList", error);
  return null;
};

export const addProspectToList = async (
  ctx: CrmContext,
  prospectId: number,
  listId: number,
): Promise<boolean> => {
  const { error } = await ctx.sb
    .from("prospect_groups")
    .upsert(
      { prospect_id: prospectId, group_id: listId, sales_id: ctx.salesId },
      { onConflict: "prospect_id,group_id", ignoreDuplicates: true },
    );
  if (error) console.error("addProspectToList", error);
  return !error;
};

/** source_record_ids of prospects on the given list (drives the list filter). */
export const fetchListMemberIds = async (
  groupId: string,
): Promise<Set<string>> => {
  const ctx = await getSessionAndSale();
  if (!ctx) return new Set();
  const { data, error } = await ctx.sb
    .from("prospect_groups")
    .select("prospects!inner(source_record_id)")
    .eq("group_id", groupId);
  if (error) {
    console.warn("fetchListMemberIds failed", error);
    return new Set();
  }
  return new Set(
    (data ?? [])
      .map((r: any) => r.prospects?.source_record_id)
      .filter(Boolean)
      .map(String),
  );
};

/** source_record_ids on ≥1 list — the "New only" exclusion set. */
export const fetchListedLeadIds = async (): Promise<Set<string>> => {
  const ctx = await getSessionAndSale();
  if (!ctx) return new Set();
  const { data, error } = await ctx.sb
    .from("prospect_groups")
    .select("prospects!inner(source_record_id)");
  if (error) {
    console.warn("fetchListedLeadIds failed", error);
    return new Set();
  }
  return new Set(
    (data ?? [])
      .map((r: any) => r.prospects?.source_record_id)
      .filter(Boolean)
      .map(String),
  );
};

// ===== Skip trace + notes (Airtable writes via the Worker) =====

/** Queue a skip trace on the lead's Airtable record. */
export const queueSkipTrace = async (item: DealLead): Promise<void> => {
  const endpoint =
    item.kind === "probate" ? "/probate-update" : "/auction-update";
  const fields =
    item.kind === "probate"
      ? { "Run Skip Trace": true }
      : {
          "Skip Trace Status": "Queued",
          "Skip Trace Queued At": new Date().toISOString(),
        };
  const res = await fetch(`${WORKER_URL}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recordId: item.id, fields }),
  });
  if (!res.ok) throw new Error(`Worker ${res.status}`);
};

/** Save Contact Notes back to the lead's Airtable record. */
export const saveLeadNote = async (
  item: Pick<DealLead, "kind" | "id">,
  value: string,
): Promise<void> => {
  const endpoint =
    item.kind === "probate" ? "/probate-update" : "/auction-update";
  const res = await fetch(`${WORKER_URL}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recordId: item.id,
      fields: { "Contact Notes": value },
    }),
  });
  if (!res.ok) throw new Error(`Worker ${res.status}`);
};
